import React, { useState, useEffect } from "react";
import AuthLayout from "../../../../layout/AuthLayout";
import {
  Box,
  Flex,
  Text,
  Group,
  TextInput,
  Button,
  Pagination,
  Modal,
  ScrollArea,
  Table,
  ActionIcon,
} from "@mantine/core";
import classes from "../../../../assets/module-css/AdminLayout.module.css";
import { useDispatch } from "react-redux";
import { updateRoute } from "../../../../store/dataRoute";
import {
  getAllCategoryRules,
  deleteCategoryRule,
} from "../../../../services/category-rules";
import { useDebouncedCallback } from "@mantine/hooks";
import LoadingData from "../../../../component/ui/LoadingData";
import NoData from "../../../../component/ui/NoData";
import { notifications } from "@mantine/notifications";
import {
  IconCheck,
  IconX,
  IconPlus,
  IconPencil,
  IconTrash,
} from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import FormCategoryRule from "./FormCategoryRule";

const defaultParameter = {
  "page[num]": 1,
  "page[limit]": 10,
  sort: "-created_at",
  include: "categories,hscodes",
};

const CategoryRules = () => {
  const dispatch = useDispatch();
  const [params, setParams] = useState(defaultParameter);
  const [dataList, setDataList] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openModalForm, setOpenModalForm] = useState(false);
  const [detailData, setDetailData] = useState(null);

  const mappingRoute = [
    { label: "/", route: null },
    { label: "Common & Global Settings", route: null },
    { label: "Category Rules", route: null },
  ];

  useEffect(() => {
    dispatch(updateRoute({ data: mappingRoute }));
  }, [dispatch]);

  const handleDelete = (val) => {
    modals.openConfirmModal({
      title: "Delete Category Rule",
      children: (
        <Text size="sm">
          Are you sure you want to delete this Category Rule? This action cannot
          be undone.
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onCancel: () => console.log("Cancel"),
      onConfirm: async () => {
        try {
          const response = await deleteCategoryRule(val.id);
          if (response.success) {
            notifications.show({
              title: "Success",
              message: "Category Rule deleted successfully",
              color: "teal",
              icon: <IconCheck />,
            });
            fetchCategoryRules();
          } else {
            notifications.show({
              title: "Failed",
              message: response.message || "Failed to delete Category Rule",
              color: "red",
              icon: <IconX />,
            });
          }
        } catch (error) {
          notifications.show({
            title: "Error",
            message: error.message || "Something went wrong",
            color: "red",
            icon: <IconX />,
          });
        }
      },
    });
  };

  const handleChangeFilter = (name, val) => {
    setParams((oldVal) => ({ ...oldVal, [name]: val }));
  };

  const handleChangePage = (val) => {
    setParams((oldVal) => ({ ...oldVal, "page[num]": val }));
  };

  const onCloseModalForm = () => {
    setOpenModalForm(false);
    setDetailData(null);
  };

  const handleAdd = () => {
    setDetailData(null);
    setOpenModalForm(true);
  };

  const handleEdit = (val) => {
    setDetailData(val);
    setOpenModalForm(true);
  };

  const fetchCategoryRules = useDebouncedCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllCategoryRules(params);

      if (response.success || response.data) {
        const startData = response.data || [];
        const countData = response.meta?.pagination?.total || 0;

        const included = response.included.hscodes || [];
        const getHscodeCode = (id) => {
          console.log("~~ ", included);
          const found =
            included[id] ||
            Object.values(included).find(
              (inc) => String(inc.id) === String(id) && inc.type === "hscode",
            );
          console.log(found);
          if (found && found.attributes) return found.attributes.code;
          return id;
        };

        console.log(getHscodeCode(489));

        const mappedData = startData.map((item) => {
          const attr = { ...(item.attributes || {}) };
          const rel = { ...(item.relationships || {}) };

          let formatStr = attr.category_format || "";
          formatStr = formatStr
            .replace(/^wholesale:/i, "")
            .replace(/:default$/i, "");
          formatStr = formatStr.split(":").join(" > ");

          return {
            id: item.id || attr.id,
            description: attr.description,
            category_format: formatStr,
            weight: attr.weight,
            weight_unit: attr.weight_unit,
            qty_cbm: attr.qty_cbm,
            hscodes: rel.hscodes || [],
            categories: rel.categories || [],
            hscode_codes: (rel.hscodes || []).map((hs) => getHscodeCode(hs.id)),
          };
        });

        setDataList(mappedData);
        setTotalData(countData);
      } else {
        setDataList([]);
        setTotalData(0);
      }
    } catch (error) {
      console.log(error);
      setDataList([]);
    } finally {
      setLoading(false);
    }
  }, 500);

  useEffect(() => {
    fetchCategoryRules();
    // eslint-disable-next-line
  }, [params]);

  const theaders = (
    <Table.Tr>
      <Table.Th>ID</Table.Th>
      <Table.Th>Description</Table.Th>
      <Table.Th>Category</Table.Th>
      <Table.Th>Weight</Table.Th>
      <Table.Th>Quantity</Table.Th>
      <Table.Th>HSCode</Table.Th>
      <Table.Th ta="center">Action</Table.Th>
    </Table.Tr>
  );

  const trows = dataList.map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td>{item.id}</Table.Td>
      <Table.Td>{item.description}</Table.Td>
      <Table.Td>{item.category_format}</Table.Td>
      <Table.Td>
        {item.weight} {item.weight_unit}
      </Table.Td>
      <Table.Td>{item.qty_cbm}</Table.Td>
      <Table.Td>{item.hscode_codes?.join(" / ")}</Table.Td>
      <Table.Td ta="center">
        <Group gap="xs" justify="center">
          <ActionIcon
            color="blue"
            variant="light"
            onClick={() => handleEdit(item)}
          >
            <IconPencil size={16} />
          </ActionIcon>
          <ActionIcon
            color="red"
            variant="light"
            onClick={() => handleDelete(item)}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <AuthLayout>
      <Box my={20}>
        <Box>
          <Flex justify="space-between" align="center" mb={30}>
            <Text className={classes.titleLayout}>Category Rules</Text>
            <Group gap="sm" align="center">
              <TextInput
                value={params["filter[description][like]"] || ""}
                placeholder="Search by Description"
                onChange={(val) => {
                  handleChangeFilter(
                    "filter[description][like]",
                    val.target.value,
                  );
                  handleChangePage(1);
                }}
                size="xs"
              />
              <Button
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={handleAdd}
              >
                Add Category Rule
              </Button>
            </Group>
          </Flex>
        </Box>

        <Box my={30}>
          <Group mb={10} gap="xs">
            <Text fz={12}>Total Data Found: </Text>
            <Text fz={12} fw="bold">
              {totalData} Category Rules{" "}
            </Text>
          </Group>

          {loading ? (
            <LoadingData />
          ) : dataList.length > 0 ? (
            <Table.ScrollContainer minWidth={800}>
              <Table verticalSpacing="sm">
                <Table.Thead>{theaders}</Table.Thead>
                <Table.Tbody>{trows}</Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ) : (
            <NoData />
          )}
        </Box>
        <Flex justify="end">
          <Pagination
            size="sm"
            onChange={handleChangePage}
            value={params["page[num]"]}
            total={
              totalData > 0 ? Math.ceil(totalData / params["page[limit]"]) : 0
            }
          />
        </Flex>
      </Box>

      <Modal
        opened={openModalForm}
        onClose={onCloseModalForm}
        centered
        closeOnClickOutside={false}
        size="70%"
        title={
          <Text fw="bold">
            {detailData ? "Edit Category Rule" : "Add Category Rule"}
          </Text>
        }
        scrollAreaComponent={ScrollArea.Autosize}
        withCloseButton={false}
      >
        {openModalForm && (
          <FormCategoryRule
            dataDetail={detailData}
            onCloseModal={onCloseModalForm}
            reloadList={fetchCategoryRules}
          />
        )}
      </Modal>
    </AuthLayout>
  );
};

export default CategoryRules;
